import { useEffect, useRef, useState } from 'react';
import { HubConnectionBuilder, type HubConnection } from '@microsoft/signalr';
import { useLocaleStore } from '@/lib/core/stores';
import { useAuthStore } from '@/lib/core/stores/useAuthStore';
import { isTokenExpired } from '@/lib/core/utils/dateTime';
import type { RideDto } from '@/services/platform/api/model';
import { getRide } from './index';
import { getConfig } from '../../../../../../config/environment';

const FALLBACK_POLL_INTERVAL_MS = 15000;

// Backoff for (re)starting the connection — both the very first `.start()` attempt
// and any restart after `withAutomaticReconnect` exhausts its own retries and gives
// up. Capped, not finite: this hook is mounted once at the app root for the whole
// ride's duration (see `useActiveRideTracking`), so nothing else will ever naturally
// remount it and retry on our behalf.
const START_RETRY_DELAYS_MS = [2000, 5000, 10000, 30000];

export interface UseRideUpdatesResult {
  ride: RideDto | undefined;
  isConnected: boolean;
}

/**
 * Subscribes to live ride/driver updates over the `/hubs/rides` SignalR hub, seeded
 * with a one-shot `getRide` call for the initial state (per that function's own
 * guidance — the hub is preferred for ongoing updates, REST for the initial load).
 *
 * While the hub connection isn't established (e.g. the backend's SignalR JWT
 * query-string auth isn't wired up yet), falls back to polling `getRide` every
 * 15s so the screen still progresses via REST rather than going stale.
 */
export function useRideUpdates(publicGuid: string | undefined): UseRideUpdatesResult {
  const locale = useLocaleStore(state => state.locale);
  const [ride, setRide] = useState<RideDto | undefined>(undefined);
  const [isConnected, setIsConnected] = useState(false);
  const isConnectedRef = useRef(false);

  useEffect(() => {
    setRide(undefined);
    setIsConnected(false);
    isConnectedRef.current = false;

    if (!publicGuid) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | undefined;
    let retryCount = 0;

    const setConnected = (value: boolean) => {
      isConnectedRef.current = value;
      if (!cancelled) setIsConnected(value);
    };

    getRide(publicGuid, locale).then(result => {
      if (!cancelled && result) setRide(result);
    });

    const connection: HubConnection = new HubConnectionBuilder()
      .withUrl(`${getConfig().platformUrl}/hubs/rides`, {
        accessTokenFactory: () => {
          const { accessToken, expiresAt } = useAuthStore.getState();
          return accessToken && !isTokenExpired(expiresAt) ? accessToken : '';
        },
      })
      .withAutomaticReconnect()
      .build();

    const scheduleStart = () => {
      const delay = START_RETRY_DELAYS_MS[Math.min(retryCount, START_RETRY_DELAYS_MS.length - 1)];
      retryTimer = setTimeout(attemptStart, delay);
    };

    const attemptStart = () => {
      if (cancelled) return;
      connection
        .start()
        .then(() => {
          retryCount = 0;
          setConnected(true);
        })
        .catch(error => {
          console.warn('useRideUpdates: hub connection failed:', error);
          retryCount += 1;
          scheduleStart();
        });
    };

    connection.on('rideUpdate', (updated: RideDto) => {
      if (!cancelled && updated?.publicGuid === publicGuid) setRide(updated);
    });
    connection.onreconnected(() => setConnected(true));
    connection.onreconnecting(() => setConnected(false));
    // Fires either on our own `.stop()` (no `error`) or once `withAutomaticReconnect`
    // exhausts its retries and gives up (`error` set) — restart from scratch in the
    // latter case so a dropped connection doesn't stay dead for the rest of the ride.
    connection.onclose(error => {
      setConnected(false);
      if (!cancelled && error) scheduleStart();
    });

    attemptStart();

    const pollTimer = setInterval(() => {
      if (cancelled || isConnectedRef.current) return;
      getRide(publicGuid, locale).then(result => {
        if (!cancelled && result) setRide(result);
      });
    }, FALLBACK_POLL_INTERVAL_MS);

    return () => {
      cancelled = true;
      clearInterval(pollTimer);
      clearTimeout(retryTimer);
      connection.stop();
    };
  }, [publicGuid, locale]);

  return { ride, isConnected };
}
